/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import { createElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";

import { Box, Button, Modal, ModalDialog, Stack, Typography } from "@mui/joy";
import {
  AppStoreButtons,
  BaseLayout,
  BlogRedirect,
  LoginButton,
  MainLayout,
  OnboardingModal,
  RootError,
} from "../components";
import { LanguageRouteHandler } from "../components/LanguageProvider";
import { LanguageRedirect } from "../components/LanguageRedirect";
import {
  EmailInput,
  VerificationInput,
} from "../components/email-verification";

import loginImage from "../assets/login-image.jpg";
import { PayPalProvider } from "../components/PayPalProvider";
import useGoogleLoginStore from "../stores/googleLoginStore";
import useMembershipStore from "../stores/membershipStore";
import {
  emailLoginToUserStatus,
  fetchAuthStatus,
  resolvePostLoginUserStatus,
  userDataToUserStatus,
} from "../utils/authStatus";
import type { UserStatus as AuthUserStatus } from "../utils/authStatus";

// Add interface for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

type UserStatus = AuthUserStatus;

// Type for Google Login Store
interface GoogleLoginStore {
  userStatus: UserStatus;
  setUserStatus: (value: UserStatus) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
  authenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  successCallback: () => void;
  setSuccessCallback: (value: () => void) => void;
  cancelCallback: () => void;
  setCancelCallback: (value: () => void) => void;
  returnCallback: () => void;
  setReturnCallback: (value: () => void) => void;
  onboardingModalVisible: boolean;
  setOnboardingModalVisible: (value: boolean) => void;
  pendingRedirectUrl: string | null;
  setPendingRedirectUrl: (value: string | null) => void;
}

let inited = false;

function authRouteDataLoader(loginRequired: boolean) {
  return async (): Promise<null> => {
    const result = await fetchAuthStatus();
    const g = useGoogleLoginStore.getState() as GoogleLoginStore;

    if (result.kind === "authenticated") {
      g.setAuthenticated(true);
      g.setUserStatus(result.userStatus);
      return null;
    }

    if (result.kind === "transient_failure") {
      return null;
    }

    g.setAuthenticated(false);
    g.setUserStatus({ isAuthenticated: false, user: undefined });

    if (!loginRequired) {
      return null;
    }

    return await new Promise<null>((resolve) => {
      if (!inited) {
        g.setVisible(true);
        g.setSuccessCallback(() => {});
        g.setReturnCallback(() => {
          window.location.href = "/";
        });
        resolve(null);
      } else {
        g.setVisible(true);
        g.setSuccessCallback(() => {
          resolve(null);
        });
        g.setReturnCallback(() => {});
      }
    });
  };
}

function GlobalModals() {
  const { t } = useTranslation();
  const location = useLocation();
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  const googleLoginModalVisible = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).visible,
  );
  const setGoogleLoginModalVisible = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).setVisible,
  );
  const setAuthenticated = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).setAuthenticated,
  );
  const setUserStatus = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).setUserStatus,
  );
  const userStatus = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).userStatus,
  );
  const successCallback = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).successCallback,
  );
  const returnCallback = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).returnCallback,
  );
  const onboardingModalVisible = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).onboardingModalVisible,
  );
  const setOnboardingModalVisible = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).setOnboardingModalVisible,
  );
  const pendingRedirectUrl = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).pendingRedirectUrl,
  );
  const setPendingRedirectUrl = useGoogleLoginStore(
    (state) => (state as GoogleLoginStore).setPendingRedirectUrl,
  );
  const fetchAndSyncMembership = useMembershipStore(
    (state) => state.fetchAndSync,
  );

  const handleEmailSubmit = async () => {
    // Prevent multiple submissions
    if (isLoading) return;

    setIsLoading(true);

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        setError("Please enter a valid email address");
        setIsLoading(false);
        return;
      }

      const response = await fetch("/api/auth/send-login-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (response.status === 201) {
        setShowEmailInput(false);
        setShowVerificationInput(true);
        setError(null);
      } else {
        // Map rate-limit/resent case to localized message
        if (response.status === 429) {
          setError(t("auth.verificationCodeSent"));
        } else {
          setError(data.error || t("common.error"));
        }
      }
    } catch (error) {
      // Ignore aborted requests
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      console.error("Failed to send verification code:", error);
      setError("Failed to send verification code");
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleVerificationSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/login-with-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, code: verificationCode }),
      });
      const data = await response.json();
      if (response.status === 201 && data.user_id) {
        const optimistic = emailLoginToUserStatus(
          email,
          Number(data.user_id),
        );
        const userStatus = await resolvePostLoginUserStatus(optimistic);
        setAuthenticated(true);
        setUserStatus(userStatus);
        setGoogleLoginModalVisible(false);

        // Check if user needs onboarding
        if (userStatus.user && userStatus.user.onboarding === false) {
          setOnboardingModalVisible(true);
        } else {
          successCallback && successCallback();
        }
      } else {
        setError(data.error || "Invalid verification code");
      }
    } catch (error) {
      console.error("Verification failed:", error);
      setError("Invalid verification code");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    inited = true;
  }, []);

  // Capture the beforeinstallprompt event for PWA installation
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  // Hydrate membership store only when user is authenticated (avoids 401 on /api/current-user for guests)
  useEffect(() => {
    if (userStatus?.isAuthenticated) {
      fetchAndSyncMembership();
    }
  }, [userStatus?.isAuthenticated, fetchAndSyncMembership]);

  // Check for onboarding URL parameter (only show if user is authenticated)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const onboardingParam = searchParams.get("onboarding");
    if (onboardingParam === "display" && userStatus?.isAuthenticated) {
      setOnboardingModalVisible(true);
    }
  }, [location.search, setOnboardingModalVisible, userStatus?.isAuthenticated]);

  const handleOnboardingComplete = async (data: {
    nickname?: string;
    invite_code?: string;
  }) => {
    try {
      const response = await fetch("/api/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        let errorMessage = "Failed to complete onboarding";

        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          // If we can't parse the error response, use a generic message
          console.error("Failed to parse error response:", parseError);
          if (response.status === 400) {
            errorMessage = t("onboarding.invalidInput");
          } else if (response.status === 403) {
            errorMessage = t("onboarding.accountProblem");
          } else if (response.status === 409) {
            errorMessage = t("onboarding.inviteCodeAlreadyUsed");
          } else if (response.status >= 500) {
            errorMessage = t("onboarding.serverError");
          }
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      const refreshed = await fetchAuthStatus();
      if (refreshed.kind === "authenticated") {
        setUserStatus(refreshed.userStatus);
      } else if (refreshed.kind === "transient_failure" && userStatus.user) {
        setUserStatus({
          ...userStatus,
          user: { ...userStatus.user, onboarding: true },
        });
      }

      // Close the onboarding modal
      setOnboardingModalVisible(false);

      console.log(
        "[GlobalModals] Onboarding complete, calling successCallback",
      );

      // Execute success callback which handles proper navigation
      successCallback && successCallback();

      // Clear pending redirect URL
      if (pendingRedirectUrl) {
        console.log(
          "[GlobalModals] Clearing pendingRedirectUrl:",
          pendingRedirectUrl,
        );
        setPendingRedirectUrl(null);
      }

      // Show success message if points were awarded
      if (result.points_awarded) {
        // You could show a success toast here
        console.log(
          `Onboarding completed! ${result.points_awarded} points awarded.`,
        );
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const renderModalContent = () => {
    const signInContent = (
      <>
        <Typography
          sx={{ mb: 1, textAlign: "center" }}
          level="h2"
          fontSize="1.5rem"
        >
          {t("auth.signIn")}
        </Typography>

        <LoginButton
          signInMethod="google.com"
          sx={{ fontSize: "1rem" }}
          onLoginSuccess={async (userData) => {
            console.log("[GlobalModals] Google login success");
            try {
              const optimistic = userDataToUserStatus(userData);
              const userStatus =
                await resolvePostLoginUserStatus(optimistic);
              console.log("[GlobalModals] Auth check result:", userStatus);
              setAuthenticated(true);
              setUserStatus(userStatus);
              setGoogleLoginModalVisible(false);

              // Check if user needs onboarding
              if (userStatus.user && userStatus.user.onboarding === false) {
                console.log(
                  "[GlobalModals] User needs onboarding, showing modal",
                );
                setOnboardingModalVisible(true);
              } else {
                console.log(
                  "[GlobalModals] User already onboarded, calling successCallback",
                );
                successCallback && successCallback();
              }
              return null;
            } catch {
              console.error("[GlobalModals] Auth check failed");
              // Auth check failed, handle silently
            }
          }}
        />
        <LoginButton
          signInMethod="email"
          sx={{ fontSize: "1rem" }}
          onLoginSuccess={async () => {
            setShowEmailInput(true);
          }}
        />
        <LoginButton
          signInMethod="anonymous"
          sx={{ fontSize: "0.875rem" }}
          onLoginSuccess={() => {
            setGoogleLoginModalVisible(false);
            returnCallback && returnCallback();
          }}
        />
        <AppStoreButtons
          sx={{ mt: 1 }}
          deferredPrompt={deferredPrompt}
          onPromptUsed={() => setDeferredPrompt(null)}
        />
      </>
    );

    const heroImage = (
      <Box
        sx={{
          width: { xs: "100%", md: "55%" },
          height: { xs: 160, md: "auto" },
          alignSelf: { xs: "center", md: "stretch" },
        }}
      >
        <Box
          component="img"
          src={loginImage}
          alt={t("auth.signIn")}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: { xs: "top", md: "center" },
            borderRadius: 0,
          }}
        />
      </Box>
    );

    if (showEmailInput) {
      return (
        <Stack
          spacing={1}
          sx={{ p: 3, width: "100%", maxWidth: 420, mx: "auto" }}
        >
          <EmailInput
            email={email}
            onEmailChange={setEmail}
            onSubmit={handleEmailSubmit}
            isLoading={isLoading}
          />
          <Button
            variant="plain"
            onClick={() => {
              setShowEmailInput(false);
              setError(null);
            }}
            size="sm"
          >
            {t("auth.back")}
          </Button>
          {error && (
            <Typography level="body-sm" color="danger">
              {error}
            </Typography>
          )}
        </Stack>
      );
    }

    if (showVerificationInput) {
      return (
        <Stack
          spacing={1}
          sx={{ p: 3, width: "100%", maxWidth: 420, mx: "auto" }}
        >
          <VerificationInput
            verificationCode={verificationCode}
            onCodeChange={setVerificationCode}
            onSubmit={handleVerificationSubmit}
            isLoading={isLoading}
          />
          <Button
            variant="plain"
            onClick={() => {
              setShowVerificationInput(false);
              setShowEmailInput(true);
              setError(null);
            }}
            size="sm"
          >
            {t("auth.back")}
          </Button>
          {error && (
            <Typography level="body-sm" color="danger">
              {error}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={0}
        sx={{ alignItems: { xs: "center", md: "stretch" } }}
      >
        {heroImage}
        <Stack spacing={1} sx={{ minWidth: 0, p: 3 }}>
          {signInContent}
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      {googleLoginModalVisible && (
        <Modal
          open={googleLoginModalVisible}
          onClose={() => {
            setGoogleLoginModalVisible(false);
            setShowEmailInput(false);
            setShowVerificationInput(false);
            setError(null);
            returnCallback && returnCallback();
          }}
        >
          <ModalDialog
            sx={{
              maxWidth: { xs: 320, md: 520 },
              width: { xs: "90vw", md: "auto" },
              p: 0,
              borderRadius: "lg",
              overflow: "hidden",
            }}
          >
            {renderModalContent()}
          </ModalDialog>
        </Modal>
      )}
      {userStatus?.isAuthenticated && (
        <OnboardingModal
          open={onboardingModalVisible}
          onClose={() => setOnboardingModalVisible(false)}
          onComplete={handleOnboardingComplete}
        />
      )}
    </>
  );
}

/**
 * Application routes
 * https://reactrouter.com/en/main/routers/create-browser-router
 */

export const router = createBrowserRouter([
  {
    path: "",
    element: (
      <>
        <Outlet />
        <GlobalModals />
      </>
    ),
    children: [
      // Language-prefixed routes for home and discover
      {
        path: ":lang",
        element: (
          <LanguageRouteHandler>
            <MainLayout loginRequired={false} />
          </LanguageRouteHandler>
        ),
        errorElement: <RootError />,
        children: [
          { index: true, lazy: () => import("./home") },
          { path: "discover", lazy: () => import("./discover") },
          { path: "tag/:tagName", lazy: () => import("./discover") },
          { path: "topic", lazy: () => import("./topic-index") },
          { path: "topic/:slug", lazy: () => import("./topic") },
          {
            path: "character-info/:id",
            async lazy() {
              const { default: Component, loader } = await import(
                "./character-info"
              );
              return {
                Component,
                loader,
              };
            },
          },
          {
            path: "creator-center",
            lazy: () => import("./creator-center"),
          },
          {
            path: "my-images",
            lazy: () => import("./my-images"),
          },
          {
            path: "user-persona",
            lazy: () => import("./my-persona"),
          },
          { path: "create-character", lazy: () => import("./create") },
          { path: "create-image", lazy: () => import("./create-images") },
          {
            path: "creator-competition",
            lazy: () => import("./creator-competition"),
          },
          {
            path: "download/apps",
            lazy: () => import("./download-apps"),
          },
          {
            path: "membership",
            async lazy() {
              const { Component } = await import("./membership");
              return {
                Component: () => (
                  <PayPalProvider>
                    <Component />
                  </PayPalProvider>
                ),
              };
            },
          },
          // Blog routes with language prefix should redirect to English-only version
          { path: "blog", element: <BlogRedirect /> },
          { path: "blog/:slug", element: <BlogRedirect /> },
        ],
        loader: authRouteDataLoader(false),
      },
      // Routes that need language redirects
      {
        path: "",
        element: <MainLayout loginRequired={false} />,
        errorElement: <RootError />,
        children: [
          { index: true, element: <LanguageRedirect /> },
          { path: "discover", element: <LanguageRedirect /> },
          { path: "tag/:tagName", element: <LanguageRedirect /> },
          { path: "character-info/:id", element: <LanguageRedirect /> },
          { path: "creator-center", element: <LanguageRedirect /> },
          { path: "my-images", element: <LanguageRedirect /> },
          { path: "user-persona", element: <LanguageRedirect /> },
          { path: "create-character", element: <LanguageRedirect /> },
          { path: "create-image", element: <LanguageRedirect /> },
          {
            path: "creator-competition",
            element: <LanguageRedirect />,
          },
          {
            path: "download/apps",
            element: <LanguageRedirect />,
          },
          { path: "settings", lazy: () => import("./settings") },
          { path: "feedback", lazy: () => import("./feedback") },
          {
            path: "moderator-center",
            lazy: () => import("./moderator-center"),
          },
          { path: "newsbreak", lazy: () => import("./newsbreak") },
          { path: "blog", lazy: () => import("./blog") },
          { path: "blog/:slug", lazy: () => import("./article") },
          { path: "topic", lazy: () => import("./topic-index") },
          { path: "topic/:slug", lazy: () => import("./topic") },
          {
            path: "membership",
            async lazy() {
              const { Component } = await import("./membership");
              return {
                Component: () => (
                  <PayPalProvider>
                    <Component />
                  </PayPalProvider>
                ),
              };
            },
          },
          {
            path: "character-info/:id",
            async lazy() {
              const { default: Component, loader } = await import(
                "./character-info"
              );
              return {
                Component,
                loader,
              };
            },
          },
          {
            path: "creator/:id",
            async lazy() {
              const { default: Component, loader } = await import(
                "./creator-info"
              );
              return {
                Component,
                loader,
              };
            },
          },
          { path: "error-page", lazy: () => import("./error-page") },
        ],
        loader: authRouteDataLoader(false),
      },
      {
        path: "",
        element: <MainLayout loginRequired={true} />,
        errorElement: <RootError />,
        children: [
          { path: "account", lazy: () => import("./account") },
          // keep edit and management behind login; public landing handled above
          { path: "admin-stats", lazy: () => import("./admin-stats") },
          {
            path: "creator-center/edit/:id",
            lazy: () => import("./edit-character"),
          },
          { path: "character/:characterId", lazy: () => import("./messages") },
          { path: "messages/:chatId", lazy: () => import("./messages") },
          { path: "character/:id/report", lazy: () => import("./report") },
          { path: "delete-account", lazy: () => import("./delete-account") },
          { path: "error-page", lazy: () => import("./error-page") },
        ],
        loader: authRouteDataLoader(true),
      },
      {
        path: "",
        element: <BaseLayout />,
        errorElement: <RootError />,
        children: [
          { path: "login", lazy: () => import("./login") },
          { path: "test-login", lazy: () => import("./test-login") },
          { path: "privacy", lazy: () => import("./privacy") },
          { path: "terms", lazy: () => import("./terms") },
          { path: "about", lazy: () => import("./about") },
        ],
      },
      {
        path: "*",
        element: <></>,
      },
    ],
  },
]);

export function Router(): JSX.Element {
  return createElement(RouterProvider, { router });
}

// Clean up on module reload (HMR)
// https://vitejs.dev/guide/api-hmr
if (import.meta.hot) {
  import.meta.hot.dispose(() => router.dispose());
}
